import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import HootlandApp from './HootlandApp';
import MintAgent from './pages/MintAgent';
import HootlandGame from './components/HootlandGame';
import HootHollow from './components/HootHollow';
import About from './pages/About';
import Tokenomics from './pages/Tokenomics';
import Collection from './pages/Collection';
import './styles/global.css';

// Global error boundary
function ErrorFallback({ error }) {
  const [showDetails, setShowDetails] = useState(false);
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      background: '#0a0a0f',
      color: '#e2e8f0',
      fontFamily: '"IBM Plex Mono", monospace',
    }}>
      <div style={{ fontSize: '64px', marginBottom: '20px' }}>🦉</div>
      <h1 style={{ 
        fontSize: '24px', 
        marginBottom: '12px',
        color: '#dc2626'
      }}>
        Something went wrong
      </h1>
      <p style={{ 
        fontSize: '14px', 
        color: '#94a3b8',
        marginBottom: '24px',
        textAlign: 'center',
        maxWidth: '400px'
      }}>
        The application encountered an error. Please refresh the page to try again.
      </p>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button 
          onClick={() => window.location.reload()}
          style={{
            background: '#dc2626',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Refresh Page
        </button>
        <button 
          onClick={() => setShowDetails(!showDetails)}
          style={{
            background: 'transparent',
            color: '#94a3b8',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </button>
      </div>
      {showDetails && (
        <pre style={{
          marginTop: '24px',
          padding: '16px',
          background: 'rgba(0,0,0,0.5)',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#f87171',
          maxWidth: '600px',
          overflow: 'auto',
          textAlign: 'left',
        }}>
          {error.message}
          {'\n\n'}
          {error.stack}
        </pre>
      )}
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

// Loading component
function PageLoader() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0a0a0f',
      color: '#e2e8f0',
    }}>
      <div style={{ fontSize: '64px', animation: 'pulse 1.5s ease-in-out infinite' }}>🦉</div>
      <div style={{ marginTop: '20px', color: '#64748b' }}>Loading Hootland...</div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

// Main App Component
export default function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial load for smooth transition
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <ErrorBoundary>
      <div style={{ minHeight: '100vh', background: '#0a0a0f' }}>
        {/* Global animated background */}
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          opacity: 0.4,
          backgroundImage: `
            linear-gradient(rgba(220,38,38,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(220,38,38,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        }} />
        
        <Routes>
          <Route path="/" element={<HootlandApp />} />
          <Route path="/app" element={<HootlandApp />} />
          <Route path="/mint" element={<MintAgent />} />
          <Route path="/world" element={<Navigate to="/hollow" replace />} />
          <Route path="/hollow" element={<HootHollow />} />
          <Route path="/about" element={<About />} />
          <Route path="/tokenomics" element={<Tokenomics />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}
